import { useState } from "react";
import { useLocation, useNavigate } from "react-router";
import { useDispatch } from "react-redux";
import { TextField, Button, Box, Typography, Alert } from "@mui/material";
import { register, setCredentials } from "../../../features/auth/authSlice";
import {
  fieldProps,
  formPanel,
  loginPaper,
  TitleTypographyLayout,
  TypographyLayout,
  underlineLink,
} from "../AuthStyles";
import theme from "../../../Constants/theme";
import { primaryButtonStyles } from "../../../Constants/buttonStyles";
import ArtPanel from "../ArtPanel";
import { sendSupportRequest } from "../../../utils/emailService";
import { getOrganization } from "../../../features/organization/orgSlice";
import Loader from "../../../Components/Loader";

function useQuery() {
  return new URLSearchParams(useLocation().search);
}

const OAuthGoogleSignup = () => {
  const query = useQuery();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const first_name = query.get("first_name") || "";
  const last_name = query.get("last_name") || "";
  const email = query.get("email") || "";

  const [organization_name, setOrganization] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleContinue = async () => {
    if (!organization_name.trim()) {
      setError("Organization name is required");
      return;
    }

    const payload = {
      first_name,
      last_name,
      email,
      organization_name,
      authentication_method: "google",
      password: "",
    };

    setLoading(true);

    const result = await dispatch(register(payload));

    setLoading(false);

    if (register.rejected.match(result)) {
      setError(result.payload || "Signup failed");
      return;
    }

    if (register.fulfilled.match(result)) {
      // notify support of successful signup
      const isoTime = new Date().toISOString();
      const description =
        `User ${first_name} ${last_name} <${email}> ` +
        `signed up for “${organization_name}” (on ${isoTime}.`;
      await sendSupportRequest({
        name: `${first_name} ${last_name}`,
        fromEmail: email,
        organization: organization_name,
        queryType: "Successful Signup",
        queryDescription: description,
        priority: "Important",
      });

      const token = result.payload.user.user.token;
      const user = result.payload.user.user;

      // save into Redux + localStorage
      dispatch(setCredentials({ user, token }));

      // get org
      const orgId = user?.organization?.["$oid"];
      if (orgId) dispatch(getOrganization(orgId));
      navigate("/dashboard/main");
    } else {
      // payload may be { error: "..." } or bare string
      setError(result.payload?.error || result.payload || "Signup failed");
    }
  };

  const handleGotoLogin = () => {
    navigate("/auth/login");
  };

  const handleGotoSignup = () => {
    navigate("/auth/signup");
  };

  if (loading === true) {
    return <Loader />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        height: "100vh",
        width: "100%",
        bgcolor: theme.main.applicationBackground,
      }}
    >
      <Box sx={formPanel}>
        <Box sx={loginPaper}>
          <Typography sx={TitleTypographyLayout} component="h1" variant="h4">
            Welcome, {first_name}!
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{ mb: 3, color: theme.textColors.dark.secondary }}
          >
            Almost there — please provide your organization name to continue.
          </Typography>

          {/* Disabled user info */}
          {/* {[
            ["First Name", first_name],
            ["Last Name", last_name],
            ["Email", email],
          ].map(([label, value]) => (
            <Box key={label} sx={{ width: "100%", mb: 2 }}>
              <Typography
                variant="caption"
                sx={{ color: theme.textColors.dark.primary, mb: 0.5 }}
              >
                {label}
              </Typography>
              <TextField fullWidth value={value} disabled {...fieldProps} />
            </Box>
          ))} */}

          {/* Organization field */}
          <Box sx={{ width: "100%", mb: 2 }}>
            <Typography
              variant="caption"
              sx={{ color: theme.textColors.dark.primary, mb: 0.5 }}
            >
              Organization Name
            </Typography>
            <TextField
              fullWidth
              placeholder="Enter your organization"
              value={organization_name}
              onChange={(e) => {
                setOrganization(e.target.value);
                if (error) setError("");
              }}
              {...fieldProps}
            />
          </Box>

          {/* General error alert */}
          {error && (
            <Alert severity="error" onClose={() => setError("")} sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            fullWidth
            variant="contained"
            sx={{ ...primaryButtonStyles, my: 2 }}
            onClick={handleContinue}
          >
            Continue
          </Button>

          <Typography variant="caption" sx={TypographyLayout}>
            Already a user?{" "}
            <Box
              onClick={handleGotoLogin}
              sx={underlineLink}
              component={"span"}
            >
              Go to Login
            </Box>
          </Typography>
          <Typography variant="caption" sx={TypographyLayout}>
            Prefer to sign up with email & password?{" "}
            <Box
              onClick={handleGotoSignup}
              sx={underlineLink}
              component={"span"}
            >
              Create an account
            </Box>
          </Typography>
        </Box>
      </Box>
      <ArtPanel />
    </Box>
  );
};

export default OAuthGoogleSignup;
